import api, { USE_MOCK, qs } from './apiClient';
import mockApi from '../mock/backend';

export const adminService = {
  listDepartments() {
    if (USE_MOCK) return mockApi.listDepartments();
    return api.get('/departments');
  },

  createDepartment(payload) {
    if (USE_MOCK) return mockApi.createDepartment(payload);
    return api.post('/departments', payload);
  },

  updateDepartment(id, patch) {
    if (USE_MOCK) return mockApi.updateDepartment(id, patch);
    return api.patch(`/departments/${id}`, patch);
  },

  listUsers() {
    if (USE_MOCK) return mockApi.listUsers();
    return api.get('/users');
  },

  getUser(id) {
    if (USE_MOCK) return mockApi.getUserDetail(id);
    return api.get(`/users/${id}`);
  },

  createUser(payload) {
    if (USE_MOCK) return mockApi.createUser(payload);
    return api.post('/users', payload);
  },

  /**
   * Which folders this person is assigned, plus the tree to choose from.
   *
   * Since folder assignment replaced blanket department access, this is what decides what
   * an Executive or a Viewer can see at all — so it lives beside the role, not somewhere
   * separate. The whole set is written back at once; see the server for why.
   */
  folderAccess(id) {
    if (USE_MOCK) return Promise.resolve({ assignedFolderIds: [], folders: [], departments: [], memberDepartmentIds: [], fromCutover: [] });
    return api.get(`/users/${id}/folder-access`);
  },

  setFolderAccess(id, folderIds) {
    if (USE_MOCK) return Promise.resolve({ assigned: folderIds.length });
    return api.put(`/users/${id}/folder-access`, { folderIds });
  },

  updateUser(id, patch) {
    if (USE_MOCK) return mockApi.updateUser(id, patch);
    return api.patch(`/users/${id}`, patch);
  },

  listRoles() {
    if (USE_MOCK) return mockApi.listRoles();
    return api.get('/roles');
  },

  listPermissions() {
    if (USE_MOCK) return mockApi.listPermissions();
    return api.get('/permissions');
  },

  updateRolePermissions(roleId, permissionKeys) {
    if (USE_MOCK) return mockApi.updateRolePermissions(roleId, permissionKeys);
    return api.patch(`/roles/${roleId}`, { permissions: permissionKeys });
  },

  createRole(payload) {
    if (USE_MOCK) return mockApi.createRole(payload);
    return api.post('/roles', payload);
  },

  deleteRole(roleId) {
    if (USE_MOCK) return mockApi.deleteRole(roleId);
    return api.delete(`/roles/${roleId}`);
  },

  auditLogs(params) {
    if (USE_MOCK) return mockApi.listAuditLogs(params);
    return api.get(`/audit-logs${qs(params)}`);
  },

  /** CSV export as a Blob. In mock mode it is generated client-side from the same rows. */
  async exportAudit() {
    if (USE_MOCK) {
      const { rows } = await mockApi.listAuditLogs({ page: 1, pageSize: 1000 });
      const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const lines = ['Timestamp,User,Email,Action,Resource type,Resource,Result,IP'];
      rows.forEach((r) =>
        lines.push([r.createdAt, r.actorName, r.actorEmail, r.action, r.resourceType, r.resourceName, r.result, r.ipAddress].map(esc).join(',')),
      );
      return new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    }
    return api.blob('/audit-logs/export');
  },

  stats() {
    if (USE_MOCK) return mockApi.adminStats();
    return api.get('/admin/stats');
  },

  /**
   * Bucket posture, and whether the objects the database points at exist.
   *
   * Separate from stats() because it makes real AWS calls — the admin screen asks for it on
   * a button rather than on every visit.
   */
  bucketHealth() {
    if (USE_MOCK) return Promise.resolve({ driver: 'local', checked: false });
    return api.get('/admin/storage/bucket');
  },

  retryOcr(documentId) {
    if (USE_MOCK) return mockApi.retryOcr(documentId);
    return api.post(`/admin/ocr/${documentId}/retry`);
  },

  /** What a CRM sync would change, without changing anything. */
  /** Send (or resend) the invitation for one user. */
  inviteUser(userId) {
    if (USE_MOCK) return mockApi.inviteUser(userId);
    return api.post(`/users/${userId}/invite`);
  },

  crmPreview() {
    if (USE_MOCK) return mockApi.crmPreview();
    return api.get('/admin/crm/preview');
  },

  crmSync(options = {}) {
    if (USE_MOCK) return mockApi.crmSync(options);
    return api.post('/admin/crm/sync', options);
  },

  /** Irreversible: removes the demonstration documents and non-CRM accounts. */
  resetDemoData(confirm) {
    if (USE_MOCK) return mockApi.resetDemoData();
    return api.post('/admin/crm/reset-demo', { confirm });
  },

  getSettings() {
    if (USE_MOCK) return mockApi.getSettings();
    return api.get('/settings');
  },

  updateSettings(patch) {
    if (USE_MOCK) return mockApi.updateSettings(patch);
    return api.patch('/settings', patch);
  },
};

export default adminService;
